import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Auditoria da folha (05/10/2026): a operadora pede [Auditar], os auditores dão OK ou
// apontam inconsistência, e só com o OK de todos o "Fechar folha" é liberado.
// Vale para a folha inteira (todas as filiais). Todo e-mail passa pela prévia.
// Backend: core/views_folha_auditoria.py (/api/bporh/folha/auditoria/).

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const ROT_EVENTO = {
  solicitacao: ['fa-paper-plane', '#0a6ea1', 'pediu a auditoria'],
  ok: ['fa-circle-check', '#1f8a56', 'deu OK'],
  inconsistencia: ['fa-triangle-exclamation', '#a11d2b', 'apontou inconsistência'],
  liberada: ['fa-lock-open', '#1f8a56', 'liberou o fechamento'],
};

export default function AuditoriaFolha({ clienteId, mes, onStatus, notificar }) {
  const [info, setInfo] = useState(null);
  const [previa, setPrevia] = useState(null);      // {acao, texto, dados, enviando}
  const [inconsist, setInconsist] = useState(null); // texto em edição
  const [cfgAberta, setCfgAberta] = useState(null); // {ativo, auditores}
  const [ocupado, setOcupado] = useState(false);
  const [verHist, setVerHist] = useState(false);
  const aviso = (m) => (notificar ? notificar(m) : null);

  const carregar = useCallback(() => {
    if (!clienteId) return;
    axios.get(`${API}/api/bporh/folha/auditoria/`, { params: { cliente_id: clienteId, mes, _: Date.now() }, headers: headers() })
      .then((r) => { setInfo(r.data); if (onStatus) onStatus(r.data); })
      .catch(() => { setInfo(null); if (onStatus) onStatus(null); });
  }, [clienteId, mes]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [carregar]);

  const post = (corpo) => axios.post(`${API}/api/bporh/folha/auditoria/`,
    { cliente_id: Number(clienteId), mes, ...corpo }, { headers: headers() });

  // prévia → (Enviar dentro da prévia); trocar "Enviar como" refaz a prévia
  const abrirPrevia = (acao, texto, operadorId) => {
    setOcupado(true);
    post({ acao, texto, operador_id: operadorId || undefined, enviar: false })
      .then((r) => {
        if (r.data.previa) setPrevia({ acao, texto, dados: r.data.previa, operadorId: r.data.previa.operador_id });
        else { aviso(`✅ ${r.data.mensagem}`); carregar(); }
      })
      .catch((e) => aviso(`❌ ${e.response?.data?.mensagem || 'Não foi possível montar a prévia'}`))
      .finally(() => setOcupado(false));
  };
  const darOk = () => {
    setOcupado(true);
    post({ acao: 'ok', enviar: false })
      .then((r) => {
        if (r.data.ultimo) setPrevia({ acao: 'ok', dados: r.data.previa, operadorId: r.data.previa.operador_id });   // último OK: e-mail à operadora
        else { aviso(`✅ ${r.data.mensagem}`); carregar(); }
      })
      .catch((e) => aviso(`❌ ${e.response?.data?.mensagem || 'Não foi possível registrar o OK'}`))
      .finally(() => setOcupado(false));
  };
  const enviarPrevia = () => {
    setPrevia((p) => ({ ...p, enviando: true }));
    post({ acao: previa.acao, texto: previa.texto, operador_id: previa.operadorId || undefined, enviar: true })
      .then((r) => { aviso(`📨 ${r.data.mensagem}`); setPrevia(null); setInconsist(null); carregar(); })
      .catch((e) => { aviso(`❌ ${e.response?.data?.mensagem || 'Não foi possível enviar'}`); setPrevia((p) => ({ ...p, enviando: false })); });
  };
  const salvarCfg = () => {
    setOcupado(true);
    post({ acao: 'config', ativo: cfgAberta.ativo, auditores: cfgAberta.auditores })
      .then((r) => { aviso(`✅ ${r.data.mensagem}`); setCfgAberta(null); carregar(); })
      .catch((e) => aviso(`❌ ${e.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setOcupado(false));
  };

  if (!info) return null;
  const { config: cfg, rodada: rod, eh_adm: adm } = info;
  if (!cfg.ativo && !adm) return null;

  const st = rod?.status;
  const podePedir = cfg.ativo && (!rod || st === 'inconsistencia');
  const auditorPendente = info.sou_auditor && st === 'pendente' && !info.ja_dei_ok;
  const cor = !cfg.ativo ? '#6c757d' : st === 'aprovada' ? '#1f8a56' : st === 'inconsistencia' ? '#a11d2b' : st === 'pendente' ? '#b58100' : '#0a6ea1';
  const fundo = !cfg.ativo ? '#f8f9fb' : st === 'aprovada' ? '#e6f7ef' : st === 'inconsistencia' ? '#fde8ec' : '#fff8e6';
  const textoStatus = !cfg.ativo ? 'Auditoria da folha desligada para este cliente'
    : !rod ? 'Esta folha passa por auditoria antes de fechar — quando terminar, clique em Auditar'
      : st === 'aprovada' ? `Folha auditada em ${rod.aprovada_em} — fechamento liberado`
        : st === 'inconsistencia' ? 'Inconsistência apontada — corrija e peça a auditoria de novo'
          : `Aguardando auditoria (rodada ${rod.rodada}) — pedida por ${rod.solicitado_por} em ${rod.solicitado_em}`;

  return (
    <div className="border rounded p-3 mb-3" style={{ background: fundo, borderColor: '#e3e8ef' }}>
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
        <div style={{ minWidth: 0 }}>
          <div className="fw-semibold" style={{ color: cor }}>
            <i className="fas fa-user-shield me-2"></i>{textoStatus}
          </div>
          {rod && (rod.auditores_rodada || []).length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {rod.auditores_rodada.map((a) => (
                <span key={a.user_id} className="badge" style={{ background: a.ok ? '#e6f7ef' : '#fff', color: a.ok ? '#116b47' : '#6c757d', border: '1px solid #dfe5ee', fontWeight: 600 }}>
                  <i className={`fas ${a.ok ? 'fa-circle-check' : 'fa-hourglass-half'} me-1`}></i>{a.nome || a.email}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {podePedir && (
            <button type="button" className="btn btn-sm btn-primary" disabled={ocupado} onClick={() => abrirPrevia('solicitar')}
              title="Envia o resumo da folha (todas as filiais) aos auditores. Você vê a prévia antes.">
              <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : 'fa-user-shield'} me-1`}></i>Auditar
            </button>
          )}
          {auditorPendente && (
            <>
              <button type="button" className="btn btn-sm btn-success" disabled={ocupado} onClick={darOk}>
                <i className="fas fa-check me-1"></i>Dar OK na auditoria
              </button>
              <button type="button" className="btn btn-sm btn-outline-danger" disabled={ocupado} onClick={() => setInconsist('')}>
                <i className="fas fa-triangle-exclamation me-1"></i>Apontar inconsistência
              </button>
            </>
          )}
          {(info.historico || []).length > 0 && (
            <button type="button" className="btn btn-sm btn-light border" onClick={() => setVerHist((x) => !x)}>
              <i className="fas fa-clock-rotate-left me-1"></i>Histórico
            </button>
          )}
          {adm && (
            <button type="button" className="btn btn-sm btn-light border" title="Só adm: liga a auditoria e escolhe quem audita"
              onClick={() => setCfgAberta({ ativo: cfg.ativo, auditores: cfg.auditores.map((a) => ({ ...a })) })}>
              <i className="fas fa-gear me-1"></i>Configurar auditoria
            </button>
          )}
        </div>
      </div>

      {inconsist !== null && (
        <div className="mt-3">
          <label className="form-label small fw-bold mb-1">O que está inconsistente? <span className="text-danger">*</span></label>
          <textarea className="form-control form-control-sm" rows={3} value={inconsist} onChange={(e) => setInconsist(e.target.value)}
            placeholder="Ex.: horas extras da Matriz somam 42 h, mas o ponto fechou 38 h — conferir o colaborador X" />
          <div className="d-flex justify-content-end gap-2 mt-2">
            <button type="button" className="btn btn-sm btn-light border" onClick={() => setInconsist(null)}>Cancelar</button>
            <button type="button" className="btn btn-sm btn-outline-danger" disabled={ocupado || inconsist.trim().length < 5}
              onClick={() => abrirPrevia('inconsistencia', inconsist)}>
              <i className="fas fa-eye me-1"></i>Ver prévia do e-mail à operadora
            </button>
          </div>
        </div>
      )}

      {verHist && (
        <ul className="list-unstyled small mt-3 mb-0">
          {info.historico.map((e, i) => {
            const [ic, c, rot] = ROT_EVENTO[e.tipo] || ['fa-circle', '#6c757d', e.tipo];
            return (
              <li key={i} className="mb-2">
                <i className={`fas ${ic} me-2`} style={{ color: c }}></i>
                <b>{e.nome}</b> {rot} · <span className="text-muted">{e.em}</span>
                {e.texto && <div className="ms-4 p-2 mt-1 rounded" style={{ background: '#fff', borderLeft: '3px solid #f1be48' }}>{e.texto}</div>}
              </li>
            );
          })}
        </ul>
      )}

      {previa && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10003 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header bg-white">
                <h5 className="modal-title" style={{ color: '#212529' }}>
                  <i className="fas fa-eye me-2" style={{ color: '#00b5cc' }}></i>Prévia do e-mail
                </h5>
                <button type="button" className="btn-close" disabled={previa.enviando} onClick={() => setPrevia(null)}></button>
              </div>
              <div className="modal-body p-0">
                <div className="small px-3 py-2 border-bottom bg-light">
                  <div><b>Assunto:</b> {previa.dados.assunto}</div>
                  <div className="d-flex align-items-center gap-2 flex-wrap my-1">
                    <b>Enviar como:</b>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} value={previa.operadorId || ''}
                      disabled={previa.enviando || ocupado}
                      onChange={(e) => abrirPrevia(previa.acao, previa.texto, e.target.value)}>
                      <option value="">Escolha quem envia...</option>
                      {(previa.dados.operadores || []).map((o) => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{!o.pronto ? ' (sem senha de app)' : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div><b>De:</b> {previa.dados.remetente} · <b>Para:</b> {previa.dados.para}</div>
                  {(previa.dados.anexos || []).length > 0 && <div><b>Anexos:</b> {previa.dados.anexos.join(', ')}</div>}
                </div>
                <iframe title="Prévia" srcDoc={previa.dados.corpo_html} sandbox="" style={{ width: '100%', height: 460, border: 'none', background: '#f4f6fa' }} />
              </div>
              <div className="modal-footer bg-white">
                {!previa.dados.pronto && <span className="small text-danger me-auto">{previa.dados.remetente}</span>}
                <button type="button" className="btn btn-sm btn-light border" disabled={previa.enviando} onClick={() => setPrevia(null)}>Cancelar</button>
                <button type="button" className="btn btn-sm btn-primary" disabled={previa.enviando || !previa.dados.pronto} onClick={enviarPrevia}>
                  <i className={`fas ${previa.enviando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-1`}></i>
                  {previa.acao === 'ok' ? 'Confirmar OK e avisar a operadora' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {cfgAberta && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10003 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header bg-white">
                <div>
                  <h5 className="modal-title" style={{ color: '#212529' }}><i className="fas fa-user-shield me-2"></i>Auditoria da folha</h5>
                  <small style={{ color: '#6c757d' }}>Vale para a folha inteira deste cliente (todas as filiais)</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setCfgAberta(null)}></button>
              </div>
              <div className="modal-body">
                <div className="form-check form-switch mb-3">
                  <input className="form-check-input" type="checkbox" id="aud-ativo" checked={cfgAberta.ativo}
                    onChange={(e) => setCfgAberta((s) => ({ ...s, ativo: e.target.checked }))} />
                  <label className="form-check-label" htmlFor="aud-ativo">A folha só fecha depois da auditoria</label>
                </div>
                <label className="form-label small fw-bold mb-1">Quem audita</label>
                {cfgAberta.auditores.map((a, i) => (
                  <div key={i} className="d-flex gap-2 mb-2">
                    <select className="form-select form-select-sm" value={a.user_id || ''}
                      onChange={(e) => {
                        const u = (info.usuarios || []).find((x) => String(x.user_id) === e.target.value);
                        setCfgAberta((s) => ({ ...s, auditores: s.auditores.map((x, j) => (j === i ? { user_id: u?.user_id, nome: u?.nome, email: x.email || u?.email || '' } : x)) }));
                      }}>
                      <option value="">Usuário do Saber...</option>
                      {(info.usuarios || []).map((u) => <option key={u.user_id} value={u.user_id}>{u.nome}</option>)}
                    </select>
                    <input className="form-control form-control-sm" placeholder="e-mail" value={a.email || ''}
                      onChange={(e) => setCfgAberta((s) => ({ ...s, auditores: s.auditores.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)) }))} />
                    <button type="button" className="btn btn-sm btn-light border" title="Remover"
                      onClick={() => setCfgAberta((s) => ({ ...s, auditores: s.auditores.filter((_, j) => j !== i) }))}>
                      <i className="fas fa-xmark"></i>
                    </button>
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline-primary"
                  onClick={() => setCfgAberta((s) => ({ ...s, auditores: [...s.auditores, { user_id: null, nome: '', email: '' }] }))}>
                  <i className="fas fa-plus me-1"></i>Adicionar auditor
                </button>
                <div className="small text-muted mt-3">
                  Quem pedir a auditoria não dá OK na própria folha. Os e-mails saem pela operadora escolhida em <b>Enviar como</b>, na prévia.
                </div>
              </div>
              <div className="modal-footer bg-white">
                <button type="button" className="btn btn-sm btn-light border" onClick={() => setCfgAberta(null)}>Cancelar</button>
                <button type="button" className="btn btn-sm btn-primary" disabled={ocupado} onClick={salvarCfg}>
                  <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-1`}></i>Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
